import { IConfig, IRestApiDatasourceBinding } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import * as React from "react";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import {
  CATEGORY_COUNT_BADGE_THRESHOLD,
  ConfigPaneKey,
  groupCategories,
  IConfigCategory,
  IConfigCategoryGroup
} from "./config-categories";
import { ConfigChangeContext, IConfigChangeRegistry } from "./config-change-tracking";
import {
  ConfigPaneContext,
  ConfigRevealContext,
  ConfigSearchSlotContext,
  IConfigRevealTarget
} from "./config-search-context";
import "./config-pane-shell.scss";

/**
 * The frame every configuration pane is drawn in: a category rail, the selected category's
 * fields, and a column of things that belong to the platform rather than to one category.
 *
 * The pane used to be one scroll of up to 23 sections; App Settings alone ran past four
 * screens, and the only way to know a section existed was to scroll onto it. The rail turns
 * that into a directory — with the group headings, the override dots and the counts that
 * make a long list scannable — and the body only ever draws one section.
 *
 * Nothing here unmounts. Hiding is CSS, at every level: one antd `Form` spans all five panes
 * and every category inside them, and `handleSubmit` rebuilds the configuration from
 * `getFieldsValue()`, so a section removed from the tree is a field silently dropped on save.
 * `ConfigSection` is the piece that enforces it — see `config-pane-parts.tsx`.
 */

export interface IConfigCategoryRailProps {
  categories: IConfigCategory[];
  activeCategoryId: string;
  onSelect: (categoryId: string) => void;
  /**
   * Categories that REPLACE a value inherited from the workspace configuration. Drawn as a
   * ring: it is a standing property of the configuration, true again next time it opens.
   */
  overriddenCategoryIds: string[];
  /**
   * Categories holding an edit that has not been saved. Drawn as a filled dot — the urgent,
   * temporary one, gone as soon as SAVE lands.
   */
  changedCategoryIds: string[];
  /** Takes back every unsaved edit in one category. The dot is the control. */
  onRevertCategory: (categoryId: string) => void;
}

export const ConfigCategoryRail = ({
  categories,
  activeCategoryId,
  onSelect,
  overriddenCategoryIds,
  changedCategoryIds,
  onRevertCategory
}: IConfigCategoryRailProps) => {
  const groups: IConfigCategoryGroup[] = groupCategories(categories);

  // Every group starts open — the rail is a directory, and a directory that hides its
  // contents until asked is a worse one. Collapsing is for putting a group you are done
  // with out of the way, so only what the reader closes is closed.
  const [collapsedGroups, setCollapsedGroups] = React.useState<string[]>([]);
  const toggleGroup = (name: string): void =>
    setCollapsedGroups((previous: string[]): string[] =>
      previous.includes(name) ? previous.filter((entry: string): boolean => entry !== name) : [...previous, name]
    );

  return (
    <nav className="kuika-config-rail" aria-label="Configuration sections">
      <KuikaSelectionIndicator activeSelector=".kuika-config-rail__item--active" />
      {groups.map((group: IConfigCategoryGroup) => {
        const isCollapsed: boolean = collapsedGroups.includes(group.name);
        // A collapsed group still holds its categories; hiding them is the stylesheet's job,
        // because a section removed from the tree is a field written back empty on save.
        const hidesActive: boolean = group.categories.some(
          (category: IConfigCategory): boolean => category.id === activeCategoryId
        );
        return (
          <React.Fragment key={group.name}>
            <button
              type="button"
              className={classNames("kuika-config-rail__group", OVERFLOW_REVEAL_HOST_CLASS)}
              aria-expanded={!isCollapsed}
              data-testid={`configCategoryGroup-${group.name}`}
              onClick={() => toggleGroup(group.name)}
            >
              <span className="kuika-config-rail__group-name">
                <KuikaOverflowText>{group.name}</KuikaOverflowText>
              </span>
              <KuikaSvg
                name="chevron-down"
                type="arrows"
                width={14}
                height={14}
                className={classNames("kuika-config-rail__chevron", {
                  "kuika-config-rail__chevron--collapsed": isCollapsed
                })}
              />
            </button>
            {group.categories.map((category: IConfigCategory) => (
              // The marks sit beside the item rather than inside it: the changed one is a
              // control of its own, and a button cannot legally hold another button.
              <div
                className={classNames("kuika-config-rail__row", {
                  // The selected category is never hidden, however its group is folded: the
                  // body is showing it, and a rail that lost its own place would be a puzzle.
                  "kuika-config-rail__row--collapsed": isCollapsed && !(hidesActive && category.id === activeCategoryId)
                })}
                key={category.id}
              >
                <button
                  type="button"
                  className={classNames("kuika-config-rail__item", OVERFLOW_REVEAL_HOST_CLASS, {
                    "kuika-config-rail__item--active": category.id === activeCategoryId
                  })}
                  data-config-category={category.id}
                  data-testid={`configCategory-${category.id}`}
                  aria-current={category.id === activeCategoryId}
                  onClick={() => onSelect(category.id)}
                >
                  <KuikaSvg
                    name={category.icon}
                    type={category.iconType}
                    width={16}
                    height={16}
                    className="kuika-config-rail__icon"
                  />
                  <span className="kuika-config-rail__label">
                    <KuikaOverflowText>{category.label}</KuikaOverflowText>
                  </span>
                  {category.fieldCount >= CATEGORY_COUNT_BADGE_THRESHOLD && (
                    <span className="kuika-config-rail__count">{category.fieldCount}</span>
                  )}
                </button>
                <span className="kuika-config-rail__marks">
                  {changedCategoryIds.includes(category.id) && (
                    <KuikaTooltip content="Undo the changes in this section" position="right">
                      <button
                        type="button"
                        className="kuika-config-rail__dot kuika-config-rail__dot--changed"
                        aria-label="Undo the unsaved changes in this section"
                        data-testid={`configCategoryChanged-${category.id}`}
                        onClick={() => onRevertCategory(category.id)}
                      />
                    </KuikaTooltip>
                  )}
                  {overriddenCategoryIds.includes(category.id) && (
                    <span
                      className="kuika-config-rail__dot kuika-config-rail__dot--overridden"
                      aria-label="Overrides the workspace configuration"
                      data-testid={`configCategoryOverridden-${category.id}`}
                    />
                  )}
                </span>
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

/**
 * What a category's values are relative to the configuration this one inherits from.
 *
 * `unknown` is not a state the badge shows — it is a category the difference cannot speak
 * about, because none of its keys live in a slice the container compares. The status block
 * and the rail dot are both omitted for it rather than guessing.
 */
export type ConfigCategoryState = "unknown" | "inherited" | "setHere" | "overridden";

export interface IConfigCategoryInheritance {
  state: ConfigCategoryState;
  /** The paths to hand back to the parent's values when Restore is pressed. */
  overriddenKeys: IConfigInheritPath[];
}

/**
 * What `handleUpdateCustomFormInput` can write back. Every inherited key a row can override
 * is a setting id, except `restApiDatasourceBindings`, which is the datasource list itself.
 */
export type ConfigRestorableValue = string | IRestApiDatasourceBinding[] | null;

/** One inherit key resolved to the slice it actually lives on. */
export interface IConfigInheritPath {
  slice: ConfigPaneKey;
  key: string;
}

/**
 * The container's `parentChildDifference`. It is shaped like a configuration and carries a
 * slice per platform, each holding the PARENT's value for every key of that slice —
 * `undefined` where the child already matches. Partial because the container only fills the
 * slices the project actually has.
 */
export type ConfigDifference = Partial<IConfig>;

/**
 * One key's parent value, or `undefined` when the child matches the parent — or when the
 * slice is absent entirely, which is the same answer as far as the category is concerned.
 *
 * It goes through a `Map` rather than indexing because the keys come from the category
 * registry as strings and the slices are real interfaces, not index-signature bags.
 */
const readDifference = (differences: ConfigDifference, path: IConfigInheritPath): unknown => {
  const slice: unknown = differences[path.slice];
  if (slice === null || typeof slice !== "object") return undefined;
  return new Map<string, unknown>(Object.entries(slice)).get(path.key);
};

/**
 * Resolve a category's inherit keys to slice/key pairs.
 *
 * A key is read against the pane's own slice unless the category names another one: Date &
 * Time Formats sits in App Settings but writes `webConfig`, and KuikAgents Integration sits
 * in Web but writes `apiConfig`.
 */
const inheritPaths = (category: IConfigCategory, paneKey: ConfigPaneKey): IConfigInheritPath[] =>
  category.inheritKeys.map((key: string): IConfigInheritPath => ({ slice: category.inheritSlice ?? paneKey, key }));

/**
 * Read one category's inheritance out of the container's parent/child difference.
 *
 * `setParentChildDifferenceOfConfigs` walks EVERY key of the parent slice and assigns what
 * `getDifferenceBetweenParentChild` returns — the parent's value when the two differ, and
 * `undefined` when they match. So the presence of a key says nothing; only a defined value
 * marks a difference, and that value is the restore target. A defined-but-empty parent value
 * means this configuration introduced the setting; a real one means it replaced one.
 */
export const readCategoryInheritance = (
  category: IConfigCategory,
  differences: ConfigDifference | undefined,
  paneKey: ConfigPaneKey
): IConfigCategoryInheritance => {
  if (category.inheritKeys.length === 0) return { state: "unknown", overriddenKeys: [] };
  if (!differences) return { state: "inherited", overriddenKeys: [] };

  const overriddenKeys: IConfigInheritPath[] = inheritPaths(category, paneKey).filter(
    (path: IConfigInheritPath): boolean => readDifference(differences, path) !== undefined
  );
  if (overriddenKeys.length === 0) return { state: "inherited", overriddenKeys: [] };

  const replacesAValue: boolean = overriddenKeys.some((path: IConfigInheritPath): boolean => {
    const parentValue: unknown = readDifference(differences, path);
    return parentValue !== null && parentValue !== "";
  });
  return { state: replacesAValue ? "overridden" : "setHere", overriddenKeys };
};

/**
 * The categories that REPLACE a value they inherit — the rail's dots.
 *
 * Only `overridden`: a category the configuration merely fills in for the first time has
 * taken nothing away from the parent, and dotting those would dot most of the rail on any
 * configuration that has been edited at all.
 */
export const getOverriddenCategoryIds = (
  categories: IConfigCategory[],
  differences: ConfigDifference | undefined,
  paneKey: ConfigPaneKey
): string[] => {
  if (!differences) return [];
  return categories
    .filter(
      (category: IConfigCategory): boolean =>
        readCategoryInheritance(category, differences, paneKey).state === "overridden"
    )
    .map((category: IConfigCategory): string => category.id);
};

/**
 * Hand every overridden key in a category back to the parent's value.
 *
 * Not a new save path: this is the per-row restore the overridden controls already offer,
 * applied across the category. `handleUpdateCustomFormInput` writes the field by its full
 * `[slice, key]` path and lets the container recompute the difference, exactly as a single
 * row's restore does — which is also why a category can restore a key on another slice.
 */
export const restoreCategory = (
  category: IConfigCategory,
  differences: ConfigDifference | undefined,
  paneKey: ConfigPaneKey,
  handleUpdateCustomFormInput: (formItemName: string[], value: ConfigRestorableValue) => void
): void => {
  if (!differences) return;
  readCategoryInheritance(category, differences, paneKey).overriddenKeys.forEach((path: IConfigInheritPath): void => {
    handleUpdateCustomFormInput([path.slice, path.key], readDifference(differences, path) as ConfigRestorableValue);
  });
};

interface IConfigCategoryStatusCopy {
  label: string;
  color: "green" | "orange" | "grey";
  description: string;
}

const STATUS_COPY: Record<Exclude<ConfigCategoryState, "unknown">, IConfigCategoryStatusCopy> = {
  inherited: {
    label: "Inherited",
    color: "grey",
    description: "Follows the workspace configuration. Setting a value here overrides it."
  },
  setHere: {
    label: "Set here",
    color: "green",
    description: "Values on this configuration, not inherited."
  },
  overridden: {
    label: "Overridden",
    color: "orange",
    description: "This section replaces the value it inherits from the workspace configuration."
  }
};

export interface IConfigCategoryStatusProps {
  inheritance: IConfigCategoryInheritance;
  /** Hands every overridden key in the category back to the parent's value. */
  onRestore: () => void;
}

/**
 * Where a category's values came from. Rendered only when the configuration HAS a parent —
 * a workspace configuration inherits from nothing, and neither does a project configuration
 * that was not created from one, so the caller omits the block entirely there rather than
 * showing a status that would always read "Set here".
 */
export const ConfigCategoryStatus = ({ inheritance, onRestore }: IConfigCategoryStatusProps) => {
  // A category the difference cannot speak about says nothing rather than guessing — its
  // settings either live outside the compared slices or have no inheritable key at all.
  if (inheritance.state === "unknown") return null;

  const copy: IConfigCategoryStatusCopy = STATUS_COPY[inheritance.state];

  return (
    <section className="kuika-config-aside__block" data-testid="configCategoryStatus">
      <KuikaTextMedium className="kuika-config-aside__title">This section</KuikaTextMedium>
      <div className="kuika-config-aside__status-row">
        <KuikaBadge content={copy.label} color={copy.color} size="small" dataTestId="configCategoryStatusBadge" />
        {inheritance.state === "overridden" && (
          <KuikaButton type="text" size="sm" data-testid="configCategoryRestoreButton" onClick={onRestore}>
            Restore
          </KuikaButton>
        )}
      </div>
      <KuikaSmallText className="kuika-config-aside__description">{copy.description}</KuikaSmallText>
    </section>
  );
};

export interface IConfigPaneAsideProps {
  children: React.ReactNode;
  /**
   * The platform's support toggle is off. Everything the column holds below that toggle
   * describes an app this configuration is not building — the icons, the splash, the PWA
   * settings — so the toggle is all that stays, and the column takes the pane's width
   * rather than sitting as a narrow stub beside an empty body.
   */
  bodyOff?: boolean;
}

/** The pane's own column: the platform switch, the category status, then platform settings. */
export const ConfigPaneAside = ({ children, bodyOff = false }: IConfigPaneAsideProps) => (
  <aside className={classNames("kuika-config-aside", { "kuika-config-aside--body-off": bodyOff })}>{children}</aside>
);

/**
 * Whether a pane has anything to put in its right column.
 *
 * On the workspace surface most panes have nothing: no platform switch (a workspace
 * configuration supports every platform), no inheritance status (it inherits from nothing),
 * and no icons or splash (those belong to a project). `!!aside` cannot tell — a fragment
 * whose every child rendered `false` is still a truthy node — so the caller says it, and the
 * shell leaves the column out rather than drawing an empty bordered 300px of nothing.
 */
export const hasAsideContent = (aside: React.ReactNode): boolean => {
  let found: boolean = false;
  React.Children.forEach(aside, (child: React.ReactNode) => {
    if (child === null || child === undefined || child === false || child === "") return;
    if (typeof child === "object" && "props" in child && child.props?.children !== undefined) {
      found = found || hasAsideContent(child.props.children);
      return;
    }
    found = true;
  });
  return found;
};

export interface IConfigPaneShellProps {
  categories: IConfigCategory[];
  activeCategoryId: string;
  onSelectCategory: (categoryId: string) => void;
  overriddenCategoryIds: string[];
  /** Every category's section, all mounted; `ConfigSection` hides the inactive ones. */
  children: React.ReactNode;
  aside?: React.ReactNode;
  /** False while the platform's support toggle is off: the rail and body have nothing to say. */
  bodyVisible?: boolean;
  /**
   * The form paths edited since the configuration was opened, from the container. The shell
   * turns them into the rail's dots; the fields turn them into an orange label.
   */
  changedPaths?: ReadonlySet<string>;
  /** Puts the named paths back to what the snapshot holds. The orange marks are the control. */
  onRevertPaths?: (paths: string[]) => void;
}

const EMPTY_PATHS: ReadonlySet<string> = new Set<string>();

export const ConfigPaneShell = ({
  categories,
  activeCategoryId,
  onSelectCategory,
  overriddenCategoryIds,
  children,
  aside,
  bodyVisible = true,
  changedPaths = EMPTY_PATHS,
  onRevertPaths
}: IConfigPaneShellProps) => {
  // Which fields sit in which category. Filled by the fields themselves as they mount, so
  // it stays exact as sections come and go, and held in a ref because a registration is not
  // news — nothing reads the map until an edit arrives, long after the last one lands.
  const fieldsByCategory: React.MutableRefObject<Map<string, Set<string>>> = React.useRef<Map<string, Set<string>>>(
    new Map<string, Set<string>>()
  );
  // A field registers from its own mount effect, which runs AFTER the rail has drawn. The
  // counter is how the rail hears about it: without one, a category whose fields arrived
  // this pass would stay undotted until something else re-rendered the shell.
  const [fieldsVersion, setFieldsVersion] = React.useState<number>(0);
  const bumpFields = React.useCallback((): void => setFieldsVersion((version: number): number => version + 1), []);

  const registry: IConfigChangeRegistry = React.useMemo(
    (): IConfigChangeRegistry => ({
      changedPaths,
      registerField: (categoryId: string, path: string): void => {
        const paths: Set<string> = fieldsByCategory.current.get(categoryId) ?? new Set<string>();
        if (paths.has(path)) return;
        paths.add(path);
        fieldsByCategory.current.set(categoryId, paths);
        bumpFields();
      },
      unregisterField: (categoryId: string, path: string): void => {
        if (!fieldsByCategory.current.get(categoryId)?.delete(path)) return;
        bumpFields();
      },
      revertPaths: (paths: string[]): void => onRevertPaths?.(paths),
      categoryPaths: (categoryId: string): string[] => [...(fieldsByCategory.current.get(categoryId) ?? [])]
    }),
    [changedPaths, onRevertPaths, bumpFields]
  );

  // A search result names a category, and only the pane holding it can select one — the rail
  // selection is pane-local state. So the shell watches for a target addressed to its own
  // pane and moves the rail; the field then scrolls itself into view, once the section it
  // sits in is the one being drawn.
  const pane: ConfigPaneKey | undefined = React.useContext(ConfigPaneContext);
  const reveal: IConfigRevealTarget | null = React.useContext(ConfigRevealContext);
  // Null in every pane but the one on screen — see `ConfigSearchSlotContext`.
  const search: React.ReactNode = React.useContext(ConfigSearchSlotContext);
  // The categories by content, not by identity: every caller builds the array fresh on each
  // render, so depending on the array itself would re-run this on every keystroke in the
  // editor. Depending on NEITHER would let a future caller pass a category set that changes
  // under a pending reveal and never notice.
  // A reveal is a one-shot, and nothing clears it: the target stays in context for the rest
  // of the session, so acting on it whenever this pane's own selection changes would snap
  // the rail back every time the reader clicked somewhere else — permanently. The token is
  // what makes it a one-shot; a second selection of the same result carries a new one.
  const handledToken: React.MutableRefObject<number> = React.useRef<number>(0);
  // By content, not by identity: every caller rebuilds this array on each render, so the
  // array itself would re-run the effect on every keystroke in the editor.
  const categoryIds: string = categories.map((category: IConfigCategory): string => category.id).join(",");
  React.useEffect((): void => {
    if (!reveal || reveal.pane !== pane || reveal.token === handledToken.current) return;
    if (!reveal.categoryId) return;
    // A category this pane is not drawing — gated away by plan, scope or project type. Left
    // unhandled on purpose: the pane may yet gain it, and the rail must not be asked for a
    // category it cannot become.
    if (!categories.some((category: IConfigCategory): boolean => category.id === reveal.categoryId)) return;
    handledToken.current = reveal.token;
    if (reveal.categoryId === activeCategoryId) return;
    onSelectCategory(reveal.categoryId);
    // Read field by field so a new object carrying the same target does not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal?.token, reveal?.pane, reveal?.categoryId, pane, activeCategoryId, categoryIds, onSelectCategory]);

  const changedCategoryIds: string[] = React.useMemo((): string[] => {
    if (changedPaths.size === 0) return [];
    return categories
      .filter((category: IConfigCategory): boolean => {
        const paths: Set<string> | undefined = fieldsByCategory.current.get(category.id);
        if (!paths) return false;
        return [...paths].some((path: string): boolean => changedPaths.has(path));
      })
      .map((category: IConfigCategory): string => category.id);
    // `fieldsVersion` is the dependency that matters as much as the other two: the map it
    // reads is a ref, so nothing else tells this to run again when a field arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, changedPaths, fieldsVersion]);

  return (
    <ConfigChangeContext.Provider value={registry}>
      <div className={classNames("kuika-config-shell", { "kuika-config-shell--body-off": !bodyVisible })}>
        {/* The rail's column, with the search above it: both are the same directory, one
            scanned and one typed at, and the box sits on the heading's line rather than in
            the header band where it read as another toolbar control. */}
        <div className="kuika-config-shell__nav">
          {search && <div className="kuika-config-shell__search">{search}</div>}
          <ConfigCategoryRail
            categories={categories}
            activeCategoryId={activeCategoryId}
            onSelect={onSelectCategory}
            overriddenCategoryIds={overriddenCategoryIds}
            changedCategoryIds={changedCategoryIds}
            onRevertCategory={(categoryId: string): void =>
              registry.revertPaths(registry.categoryPaths(categoryId).filter((path: string) => changedPaths.has(path)))
            }
          />
        </div>
        <div className="kuika-config-shell__body">{children}</div>
        {hasAsideContent(aside) && <ConfigPaneAside bodyOff={!bodyVisible}>{aside}</ConfigPaneAside>}
      </div>
    </ConfigChangeContext.Provider>
  );
};
